// Geolonia APIのデータ構造に対応した型定義
export interface GeoloniaPrefecture {
  code: number;
  pref: string;
  pref_k: string;
  pref_r: string;
  point: [number, number];
  cities: GeoloniaCity[];
}

export interface GeoloniaCity {
  code: number;
  city: string;
  city_k: string;
  city_r: string;
  ward?: string;
  ward_k?: string;
  ward_r?: string;
  county?: string;
  county_k?: string;
  county_r?: string;
  point: [number, number];
}

export interface GeoloniaResponse {
  meta: {
    updated: number;
  };
  data: GeoloniaPrefecture[];
}

// フロントエンド用の簡略化された型定義
export interface Prefecture {
  code: string;
  name: string;
  cities: City[];
}

export interface City {
  code: string;
  name: string;
  fullName?: string; // ward情報を含む完全な名前
}

// Geoloniaデータを簡略化形式に変換
export const convertGeoloniaToSimpleFormat = (geoloniaData: GeoloniaResponse): Prefecture[] => {
  return geoloniaData.data.map(pref => ({
    code: pref.code.toString().padStart(2, '0'),
    name: pref.pref,
    cities: pref.cities.map(city => ({
      code: city.code.toString(),
      name: city.ward ? `${city.city} ${city.ward}` : city.city,
      fullName: city.ward 
        ? `${city.city} ${city.ward}` 
        : city.county 
          ? `${city.county} ${city.city}` 
          : city.city
    }))
  }));
};

// S3やAPIからデータを取得する関数
export const fetchAddressData = async (baseUrl?: string): Promise<Prefecture[]> => {
  const url = baseUrl 
    ? `${baseUrl}/ja.json`
    : 'https://japanese-addresses-v2.geoloniamaps.com/api/ja.json';

  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`住所データの取得に失敗しました: ${response.status}`);
    }
    
    const geoloniaData: GeoloniaResponse = await response.json();
    return convertGeoloniaToSimpleFormat(geoloniaData);
  } catch (error) {
    console.error('住所データの取得エラー:', error);
    throw error;
  }
};

// キャッシュ機能付きデータ取得
let cachedPrefectures: Prefecture[] | null = null;
let cacheTimestamp: number | null = null;
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24時間

export const getCachedAddressData = async (baseUrl?: string): Promise<Prefecture[]> => {
  const now = Date.now();
  
  // キャッシュが有効な場合は返す
  if (cachedPrefectures && cacheTimestamp && (now - cacheTimestamp) < CACHE_DURATION) {
    return cachedPrefectures;
  }
  
  // 新しいデータを取得してキャッシュ
  try {
    const prefectures = await fetchAddressData(baseUrl);
    cachedPrefectures = prefectures;
    cacheTimestamp = now;
    return prefectures;
  } catch (error) {
    // エラーが発生した場合、古いキャッシュがあれば返す
    if (cachedPrefectures) {
      console.warn('最新データの取得に失敗しましたが、キャッシュデータを使用します');
      return cachedPrefectures;
    }
    throw error;
  }
};

// ユーティリティ関数
export const getPrefectureByCode = (prefectures: Prefecture[], code: string): Prefecture | undefined => {
  return prefectures.find(p => p.code === code);
};

export const getCitiesByPrefectureCode = (prefectures: Prefecture[], prefectureCode: string): City[] => {
  const prefecture = getPrefectureByCode(prefectures, prefectureCode);
  return prefecture?.cities || [];
};

export const getCityByCode = (prefectures: Prefecture[], prefectureCode: string, cityCode: string): City | undefined => {
  const prefecture = getPrefectureByCode(prefectures, prefectureCode);
  return prefecture?.cities.find(c => c.code === cityCode);
};
